import React, { useEffect, useState, useRef } from 'react';
import './styles.css';
import { useRecoilValue } from 'recoil';
import songState from 'store/song';
import { SongObject } from 'store/song/types';

interface SetlistData {
  current: string;
  reserve: string;
  setlist: string;
}

const SetlistDisplayOverlay: React.FC = () => {
  const songs = useRecoilValue(songState);
  const [currentSkin, setCurrentSkin] = useState('simpleBlackB');
  const [setlistData, setSetlistData] = useState<SetlistData>({
    current: '---',
    reserve: '',
    setlist: '',
  });
  const [nowClass, setNowClass] = useState('empty');
  const stlLogRef = useRef<HTMLOListElement>(null);
  const stlNowRef = useRef<HTMLParagraphElement>(null);
  // ScrollOverflow.js logic for setlist and now singing
  useEffect(() => {
    // Add animation styles to document head (once)
    let style = document.getElementById('scrollOverflowStyle');
    if (!style) {
      style = document.createElement('style');
      style.id = 'scrollOverflowStyle';
      style.innerText = `
      .scrollOut {
        animation: out 1s linear;
        animation-fill-mode: forwards;
      }
      @keyframes out {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
      .scrollPop { animation: pop 0.5s linear; }
      @keyframes pop {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      `;
      document.head.appendChild(style);
    }

    function checkListHeight() {
      const stlLog = stlLogRef.current;
      if (!stlLog) return;
      const sa = stlLog.scrollHeight - stlLog.clientHeight;
      if (sa > 0) {
        scrollList(sa);
      } else {
        stlLog.scrollTo(0, 0);
        setTimeout(checkListHeight, 1000);
      }
    }

    function scrollList(v: number) {
      const stlLog = stlLogRef.current;
      if (!stlLog) return;
      stlLog.className = 'scrollOut';
      setTimeout(() => {
        stlLog.scrollTo(0, 0);
        stlLog.className = 'scrollPop';
      }, 1000);
      let i = 0;
      while (i < v) {
        setTimeout(() => {
          stlLog.scrollBy(1, 1);
        }, i * 40 + 4000);
        i++;
        if (i === v) {
          setTimeout(checkListHeight, i * 40 + 10000);
        }
      }
    }

    function checkSongWidth() {
      const stlNow = stlNowRef.current;
      if (!stlNow) return;
      const sa = stlNow.scrollWidth - stlNow.clientWidth;
      if (sa > 0) {
        scrollSong(sa);
      } else {
        stlNow.scrollTo(0, 0);
        setTimeout(checkSongWidth, 1000);
      }
    }

    function scrollSong(v: number) {
      const stlNow = stlNowRef.current;
      if (!stlNow) return;
      let i = 0;
      while (i < v) {
        setTimeout(() => {
          stlNow.scrollBy(1, 1);
        }, i * 20 + 4000);
        i++;
        if (i === v) {
          setTimeout(() => {
            stlNow.className = 'scrollOut';
          }, i * 20 + 6000);
          setTimeout(() => {
            stlNow.className = 'scrollPop';
            stlNow.scrollBy(-v, -v);
          }, i * 20 + 7000);
          setTimeout(checkSongWidth, i * 20 + 10000);
        }
      }
    }

    // Start scroll checks after DOM update
    setTimeout(() => {
      checkListHeight();
      checkSongWidth();
    }, 500);

    // Optionally, cleanup timeouts on unmount
    return () => {
      // No explicit cleanup for setTimeouts here
    };
  }, []);

  useEffect(() => {
    // Load saved skin from localStorage
    const savedSkin = localStorage.getItem('setlistaSkin');
    if (savedSkin) {
      changeSkin(savedSkin);
    }
  }, []);

  useEffect(() => {
    const setlistSongs = formatSetlist(songs);
    syncList(setlistSongs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs]);

  const formatSetlist = (songs: SongObject[]): string[] => {
    // Setlista format is [currentSong, reserveList, setlist]
    // Lists are just the names separated by newlines.
    // Everything has to be URI Encoded
    const songsCopy = songs.slice(0);
    let currentSongName = '';
    if (songsCopy.length > 0) {
      currentSongName = songsCopy.pop()!.name;
    }

    const setlist = songsCopy.map((song) => song.name).join('\n');

    return [encodeURI(currentSongName), encodeURI(''), encodeURI(setlist)];
  };

  const syncList = (data: string[]) => {
    const newData: SetlistData = {
      current: data[0] ? decodeURI(data[0]) : '---',
      reserve: data[1] ? decodeURI(data[1]) : '',
      setlist: data[2] ? decodeURI(data[2]) : '',
    };

    // Apply timestamp formatting
    newData.current = timeStampChange(newData.current);
    newData.setlist = timeStampChange(newData.setlist);

    setSetlistData(newData);
    changeNowClass(newData.current);
  };

  const timeStampChange = (text: string): string => {
    return text.replace(/ \- \[(\d+)\:(\d+)\]/g, '<time>$1:$2</time>');
  };

  const changeSkin = (skin: string) => {
    setCurrentSkin(skin);
    localStorage.setItem('setlistaSkin', skin);
  };

  const changeNowClass = (currentText: string) => {
    if (currentText === '---') {
      setNowClass('empty');
    } else {
      setNowClass((prev) => (prev === 'even' ? 'odd' : 'even'));
    }
  };

  const renderSetlistItems = (items: string) => {
    if (!items) return null;
    return items
      .split('\n')
      .map((item, index) => (
        <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
      ));
  };

  return (
    <div className={`view ${currentSkin}`}>
      <section id="blkNow" className={nowClass}>
        <h2>Now Singing</h2>
        <p
          id="stlNow"
          ref={stlNowRef}
          dangerouslySetInnerHTML={{ __html: setlistData.current }}
        />
      </section>
      <section id="blkLog">
        <h2>Set List</h2>
        <ol id="stlLog" ref={stlLogRef}>
          {renderSetlistItems(setlistData.setlist)}
        </ol>
      </section>
      <section id="blkRes">
        <h2>Reserve</h2>
        <ol id="stlRes">{renderSetlistItems(setlistData.reserve)}</ol>
      </section>
    </div>
  );
};

export default SetlistDisplayOverlay;
